// CTSK-363 Slice A — projects list multi-source affordance.
//
// Replaces the comma-separated-string-in-sourceUrl workaround (broken
// "open" clicks: a comma-joined value parses as ONE nonsense URL, it
// doesn't throw) with a real `sources: {url, label?}[]` array plus a
// `defaultSourceIndex` pointing at the row-inline default.
//
// Two components, same split as SessionRefs.tsx:
//
//   · ProjectSourcesCell — read surface for the Projects list row.
//     Default source renders inline (as before); additional sources
//     collapse into a small "+N" bubble that expands to a popover with
//     each source individually clickable.
//
//   · ProjectSourcesEditor — authoring surface inside the Edit Project
//     dialog's "More fields" expand (same slot as SessionRefsEditor).
//     "+ source" appends, ✕ removes, a radio picks the default. Local
//     list edits only; the dialog PATCHes the full array on Save.

import { useEffect, useRef, useState } from 'react';
import type { ProjectSource } from '@captain-tasko/shared';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — installed via pnpm install
import { ExternalLink, X } from 'lucide-react';

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * Read surface for one row's Source column. `sources`/`defaultSourceIndex`
 * are the authoritative shape; `legacySourceUrl` is a defensive fallback
 * for a project that somehow has neither (pre-migration edge case, or a
 * row the compat-read derivation couldn't resolve) — A.5: never crash,
 * just render nothing or the raw legacy value.
 */
export function ProjectSourcesCell({
  sources,
  defaultSourceIndex,
  legacySourceUrl,
}: {
  sources: ProjectSource[];
  defaultSourceIndex: number;
  legacySourceUrl?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // No sources[] at all — fall back to the legacy single value so a
  // not-yet-migrated (or migration-failed) row still renders something
  // clickable instead of a blank cell.
  const effective = sources.length > 0 ? sources : legacySourceUrl ? [{ url: legacySourceUrl }] : [];

  if (effective.length === 0) {
    return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  }

  const clampedIndex = Math.min(Math.max(defaultSourceIndex, 0), effective.length - 1);
  const primary = effective[clampedIndex]!;
  const others = effective.filter((_, i) => i !== clampedIndex);

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <a
        href={primary.url}
        target="_blank"
        rel="noopener noreferrer"
        title={primary.label ? `${primary.label} — ${primary.url}` : primary.url}
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 12,
          color: 'var(--accent-primary)',
          textDecoration: 'none',
        }}
      >
        <ExternalLink size={12} strokeWidth={1.75} aria-hidden />
        {primary.label ?? hostOf(primary.url)}
      </a>

      {others.length > 0 && (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={`${others.length} more source${others.length === 1 ? '' : 's'}`}
          title={`${others.length} more source${others.length === 1 ? '' : 's'}`}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          style={{
            fontSize: 10,
            padding: '0 5px',
            borderRadius: 8,
            background: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)',
            color: 'var(--accent-primary)',
            border: 'none',
            cursor: 'pointer',
            lineHeight: '16px',
          }}
        >
          +{others.length}
        </button>
      )}

      {open && others.length > 0 && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            zIndex: 50,
            minWidth: 200,
            maxWidth: 320,
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 16px rgba(0,0,0,.15)',
            padding: 6,
          }}
        >
          {others.map((s, i) => (
            <a
              // eslint-disable-next-line react/no-array-index-key
              key={`${s.url}-${i}`}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              title={s.url}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 6px',
                borderRadius: 4,
                fontSize: 12,
                color: 'var(--accent-primary)',
                textDecoration: 'none',
                overflowWrap: 'anywhere',
              }}
            >
              <ExternalLink size={12} strokeWidth={1.75} aria-hidden />
              {s.label ?? s.url}
            </a>
          ))}
        </div>
      )}
    </span>
  );
}

/**
 * Editor for the Edit Project dialog (More fields — same slot as
 * SessionRefsEditor). Pure controlled component — owns no server
 * state. Caller PATCHes {sources, defaultSourceIndex} on Save.
 */
export function ProjectSourcesEditor({
  sources,
  defaultSourceIndex,
  onChange,
}: {
  sources: ProjectSource[];
  defaultSourceIndex: number;
  onChange: (next: { sources: ProjectSource[]; defaultSourceIndex: number }) => void;
}) {
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const trimmedUrl = newUrl.trim();
  let addError: string | null = null;
  if (trimmedUrl) {
    try {
      new URL(trimmedUrl);
    } catch {
      addError = 'Must be a valid URL (include https://)';
    }
    if (!addError && sources.some((s) => s.url === trimmedUrl)) {
      addError = 'This URL is already on the project.';
    }
  }

  function add() {
    if (!trimmedUrl || addError) return;
    const label = newLabel.trim();
    const next = [...sources, { url: trimmedUrl, ...(label ? { label } : {}) }];
    onChange({ sources: next, defaultSourceIndex });
    setNewUrl('');
    setNewLabel('');
  }

  function remove(idx: number) {
    const next = sources.filter((_, i) => i !== idx);
    // Keep the default pointer valid: if the removed row was before the
    // default, shift down; if it WAS the default, fall back to 0.
    let nextDefault = defaultSourceIndex;
    if (idx === defaultSourceIndex) nextDefault = 0;
    else if (idx < defaultSourceIndex) nextDefault = defaultSourceIndex - 1;
    onChange({ sources: next, defaultSourceIndex: Math.min(nextDefault, Math.max(next.length - 1, 0)) });
  }

  function makeDefault(idx: number) {
    onChange({ sources, defaultSourceIndex: idx });
  }

  return (
    <div>
      {sources.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '0 0 8px', padding: 0 }}>
          {sources.map((s, idx) => (
            <li
              // eslint-disable-next-line react/no-array-index-key
              key={`${s.url}-${idx}`}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}
            >
              <input
                type="radio"
                name="proj-default-source"
                checked={idx === defaultSourceIndex}
                onChange={() => makeDefault(idx)}
                aria-label={`Make "${s.label ?? s.url}" the default source`}
                title="Default (shown inline on the row)"
              />
              <code
                title={s.url}
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  flex: 1,
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                }}
              >
                {s.label ? `${s.label} — ${s.url}` : s.url}
              </code>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                aria-label={`Remove source ${s.url}`}
                title="Remove (applies on Save)"
                style={{ padding: '2px 6px', display: 'inline-flex', alignItems: 'center' }}
                onClick={() => remove(idx)}
              >
                <X size={12} strokeWidth={1.75} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        <input
          aria-label="New source URL"
          type="url"
          inputMode="url"
          placeholder="https://github.com/owner/repo"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          aria-invalid={!!addError}
          aria-describedby={addError ? 'proj-source-add-err' : undefined}
          style={{ flex: 2, minWidth: 0 }}
        />
        <input
          aria-label="Label (optional)"
          placeholder="Label (optional)"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button type="button" className="btn btn--sm" onClick={add} disabled={!trimmedUrl || !!addError}>
          + source
        </button>
      </div>
      {addError && (
        <div id="proj-source-add-err" style={{ fontSize: 12, color: 'var(--danger-primary)', marginTop: 4 }}>
          {addError}
        </div>
      )}
      {sources.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          No sources yet. The first one added becomes the default automatically.
        </div>
      )}
    </div>
  );
}
